import { z } from 'zod';
import { REGION_VALUES } from '@/shared/utils/regions';
import { env } from '@/shared/lib/env';

export const adminParcelSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'parcels.form.errors.nameRequired')
    .max(120, 'parcels.form.errors.tooLong'),
  // Owner spec (2026-06-01): trek_id is 5..255 chars. Regex stays permissive
  // (latin/digits/hyphen) — frontend separately upper-cases on type so any
  // lowercase scanner output normalises before validation.
  trek_id: z
    .string()
    .trim()
    .min(5, 'parcels.form.errors.trekIdLength')
    .max(255, 'parcels.form.errors.trekIdLength')
    .regex(/^[A-Za-z0-9-]+$/u, 'parcels.form.errors.trekIdInvalid'),
  store_name: z.string().trim().max(120, 'parcels.form.errors.tooLong').optional().or(z.literal('')),
  address_name: z
    .string()
    .trim()
    .min(2, 'parcels.form.errors.addressNameRequired')
    .max(120, 'parcels.form.errors.tooLong'),
  region: z.enum(REGION_VALUES),
  city: z
    .string()
    .trim()
    .min(2, 'parcels.form.errors.cityRequired')
    .max(80, 'parcels.form.errors.tooLong'),
  street: z
    .string()
    .trim()
    .min(2, 'parcels.form.errors.streetRequired')
    .max(120, 'parcels.form.errors.tooLong'),
  building: z
    .string()
    .trim()
    .min(1, 'parcels.form.errors.buildingRequired')
    .max(40, 'parcels.form.errors.tooLong'),
  apartment: z.string().trim().max(40, 'parcels.form.errors.tooLong').optional().or(z.literal('')),
  additional_info: z
    .string()
    .trim()
    .max(500, 'parcels.form.errors.tooLong')
    .optional()
    .or(z.literal('')),
  first_name: z
    .string()
    .trim()
    .min(2, 'parcels.form.errors.firstNameRequired')
    .max(60, 'parcels.form.errors.tooLong'),
  last_name: z
    .string()
    .trim()
    .min(2, 'parcels.form.errors.lastNameRequired')
    .max(60, 'parcels.form.errors.tooLong'),
  middle_name: z
    .string()
    .trim()
    .min(2, 'parcels.form.errors.middleNameRequired')
    .max(60, 'parcels.form.errors.tooLong'),
  phone_number: z
    .string()
    .trim()
    .min(7, 'parcels.form.errors.phoneTooShort')
    .max(20, 'parcels.form.errors.tooLong')
    .regex(/^\+?[0-9\s()-]+$/u, 'parcels.form.errors.phoneInvalid'),
  passport_series: z
    .string()
    .min(1, 'parcels.form.errors.passportRequired')
    .length(9, 'parcels.form.errors.passportInvalid')
    .regex(/^[A-Z]{2}[0-9]{7}$/, 'parcels.form.errors.passportInvalid'),
  pinfl: z
    .string()
    .trim()
    .length(env.pinflLength, 'parcels.form.errors.pinflLength')
    .regex(/^\d+$/u, 'parcels.form.errors.pinflDigitsOnly'),
});

export type AdminParcelFormValues = z.infer<typeof adminParcelSchema>;

export const emptyAdminParcelValues: AdminParcelFormValues = {
  name: '',
  trek_id: '',
  store_name: '',
  address_name: '',
  region: 'TASHKENT_CITY',
  city: '',
  street: '',
  building: '',
  apartment: '',
  additional_info: '',
  first_name: '',
  last_name: '',
  middle_name: '',
  phone_number: '',
  passport_series: '',
  pinfl: '',
};

// Create requires a resolved Cargo ID (UUID). `cargo_code` is UI-only and not submitted.
// `confirmed_for_shipping` (owner spec 2026-06-01) lets the admin skip the
// IN_PROGRESS workflow stage when the warehouse already holds the box.
// `add_to_warehouse` (owner spec 2026-06-01) tells the backend to also
// auto-create an OwnerlessProduct with this trek_id so the parcel lands
// in the warehouse the same way a scanner add would have.
export const adminParcelCreateSchema = adminParcelSchema.extend({
  cargo_id: z.string().uuid('parcels.form.errors.cargoIdRequired'),
  cargo_code: z.string().optional(),
  confirmed_for_shipping: z.boolean().optional(),
  add_to_warehouse: z.boolean().optional(),
});

export type AdminParcelCreateFormValues = z.infer<typeof adminParcelCreateSchema>;

export const emptyAdminParcelCreateValues: AdminParcelCreateFormValues = {
  ...emptyAdminParcelValues,
  cargo_id: '',
  cargo_code: '',
  confirmed_for_shipping: false,
  add_to_warehouse: false,
};

// Edit extends the base parcel schema with the same confirmed_for_shipping
// flag the create form exposes. Owner spec (2026-06-01): admins can tick
// the checkbox from the edit page too as long as the parcel isn't already
// inside a package.
export const adminParcelEditSchema = adminParcelSchema.extend({
  confirmed_for_shipping: z.boolean().optional(),
});

export type AdminParcelEditFormValues = z.infer<typeof adminParcelEditSchema>;

export const emptyAdminParcelEditValues: AdminParcelEditFormValues = {
  ...emptyAdminParcelValues,
  confirmed_for_shipping: false,
};
